<template>
  <section class="relative h-screen w-screen overflow-hidden flex flex-col items-center justify-center">
    <!-- Background -->
    <div class="absolute top-0 left-0 w-full h-full">
      <img src="https://4kwallpapers.com/images/wallpapers/glossy-abstract-3840x2160-9602.jpg" 
           alt="Background" 
           class="w-full h-full object-cover"/>
      <div class="absolute top-0 left-0 w-full h-full bg-black opacity-0"></div>
    </div>

    <!-- Formulaire -->
    <div class="relative w-full max-w-lg p-12 flex flex-col gap-6 rounded-2xl shadow-2xl bg-white/20 border border-white/30 z-10 backdrop-blur-md">
      <img class="h-24 mx-auto" src="../assets/datafuse.svg" alt="Logo Datafuse">

      <h3 class="text-2xl font-semibold text-center text-white">
        {{ token ? "Réinitialisation du mot de passe" : forgotPassword ? "Mot de passe oublié" : "Connexion" }}
      </h3>
      <span class="text-gray-100 text-center" v-if="!token && !forgotPassword">
        Connectez-vous avec votre adresse email et votre mot de passe
      </span>

      <div class="w-full space-y-4 mt-6">
        <!-- Email -->
        <div class="relative">
          <input v-model="email" type="email" placeholder="Adresse email"
            class="peer py-3 ps-10 block w-full bg-transparent border-b-2 border-gray-300 text-base text-white placeholder-gray-200 focus:border-b-blue-400 focus:ring-0"
          />
        </div>

        <!-- Login fields -->
        <div v-if="!token && !forgotPassword">
          <div class="relative">
            <input v-model="mot_de_passe" :type="showPassword ? 'text' : 'password'" placeholder="Mot de passe"
              class="peer py-3 ps-10 pr-12 block w-full bg-transparent border-b-2 border-gray-300 text-base text-white placeholder-gray-200 focus:border-b-blue-400 focus:ring-0"
            />
            <button type="button" class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-200" @click="togglePassword">
              <span>{{ showPassword ? '👁️' : '🙈' }}</span>
            </button>
          </div>

          <div class="text-right">
            <a href="#" class="text-sm text-blue-200 hover:underline" @click.prevent="forgotPassword = true">Mot de passe oublié ?</a>
          </div>
        </div>

        <!-- Reset password fields -->
        <div v-if="token">
          <input v-model="newPassword" type="password" placeholder="Nouveau mot de passe"
            class="peer py-3 ps-10 block w-full bg-transparent border-b-2 border-gray-300 text-base text-white placeholder-gray-200 focus:border-b-blue-400 focus:ring-0"
          />
          <input v-model="confirmPassword" type="password" placeholder="Confirmez le mot de passe"
            class="peer py-3 ps-10 block w-full bg-transparent border-b-2 border-gray-300 text-base text-white placeholder-gray-200 focus:border-b-blue-400 focus:ring-0"
          />
        </div>

        <!-- Forgot password fields -->
        <div v-if="forgotPassword && !token">
          <p class="text-gray-200 text-sm mb-2">Entrez votre email pour recevoir un lien de réinitialisation :</p>
        </div>

        <!-- Button -->
        <button type="button"
          class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-full font-medium text-lg transition"
          @click="token ? resetPassword() : forgotPassword ? sendResetLink() : login()"
        >
          {{ token ? "Réinitialiser" : forgotPassword ? "Envoyer le lien" : "Se connecter" }}
        </button>

        <!-- Message -->
        <p v-if="message" class="mt-2 text-center text-green-300">{{ message }}</p>
      </div>
    </div>

    <!-- Clients -->
    <div class="mt-8 flex items-center gap-2 z-10">
      <div class="flex -space-x-3">
        <img v-for="(avatar, index) in avatars" :key="index" :src="avatar" class="w-12 h-12 rounded-full border-2 border-white object-cover">
      </div>
      <span class="text-white text-sm font-medium mt-2">+20 clients satisfaits</span>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from '../main.js';

const email = ref("");
const mot_de_passe = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const forgotPassword = ref(false);
const message = ref("");
const router = useRouter();
const route = useRoute();

const token = ref(null);

onMounted(() => {
  token.value = route.params.token || null;
});

const avatars = [
  "https://randomuser.me/api/portraits/women/81.jpg",
  "https://randomuser.me/api/portraits/men/82.jpg",
  "https://randomuser.me/api/portraits/women/83.jpg",
  "https://randomuser.me/api/portraits/men/84.jpg",
  "https://randomuser.me/api/portraits/women/85.jpg",
];

const togglePassword = () => showPassword.value = !showPassword.value;

const login = async () => {
  if (!email.value || !mot_de_passe.value) return toast.error("Tous les champs sont requis");
  try {
    const res = await fetch(`https://datafuseweb-95a5588f8542.herokuapp.com/api/clients/connexion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.value, password: mot_de_passe.value }),
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data.message || "Erreur lors de la connexion");

    localStorage.setItem("token", data.token);
    localStorage.setItem("client", JSON.stringify(data.client));

    toast.success("Connexion réussie !");
    router.push("/");
  } catch (err) {
    console.error(err);
    toast.error("Erreur réseau, réessayez plus tard");
  }
};

const sendResetLink = async () => {
  if (!email.value) return toast.error("Email requis");
  try {
    const res = await fetch(`https://datafuseweb-95a5588f8542.herokuapp.com/api/clients/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.value }),
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data.message || "Erreur lors de l'envoi du lien");

    message.value = "Lien de réinitialisation envoyé ! Vérifiez votre boîte mail.";
  } catch (err) {
    console.error(err);
    toast.error("Erreur réseau, réessayez plus tard");
  }
};

const resetPassword = async () => {
  if (!newPassword.value || !confirmPassword.value) return toast.error("Tous les champs sont requis");
  if (newPassword.value !== confirmPassword.value) return toast.error("Les mots de passe ne correspondent pas");

  try {
    const res = await fetch(`https://datafuseweb-95a5588f8542.herokuapp.com/api/clients/reset-password/${token.value}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        newPassword: newPassword.value,
        confirmPassword: confirmPassword.value,
      }),
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data.message || "Erreur lors de la réinitialisation");

    message.value = "Mot de passe réinitialisé avec succès ! Redirection...";
    setTimeout(() => router.push("/login"), 2000);
  } catch (err) {
    console.error(err);
    toast.error("Erreur réseau, réessayez plus tard");
  }
};
</script>
